import {
  type CreatePartnerAccountRequest,
  type Partner,
  type PartnerAccountBody,
  type PartnerCategory,
} from '@/api/routes/partners/partners.api';
import { type OnboardingFormValues, type OnboardingStepKey } from '@/domain/onboarding/types';
import { translatedMap } from '@/i18n';
import { type User } from '@/types/user';
import { format } from '@/utils/data';
import { onlyDigits } from '@/utils/document';

export const ONBOARDING_STEPS: OnboardingStepKey[] = ['organization', 'contact', 'review'];

/** Fields validated before leaving each step (the review step submits everything). */
export const ONBOARDING_STEP_FIELDS: Record<OnboardingStepKey, (keyof OnboardingFormValues)[]> = {
  organization: ['logoUrl', 'displayName', 'legalName', 'taxId', 'category', 'description'],
  contact: ['contactName', 'contactEmail', 'contactPhone', 'city', 'state', 'website', 'instagram'],
  review: ['acceptTerms'],
};

export const PARTNER_CATEGORIES: PartnerCategory[] = [
  'events',
  'running_club',
  'bike_fit',
  'nutrition',
  'physio',
  'gear',
  'sports_media',
  'other',
];

export const partnerCategoryLabel = translatedMap('domain.onboarding.utils', {
  events: 'category.events',
  running_club: 'category.running_club',
  bike_fit: 'category.bike_fit',
  nutrition: 'category.nutrition',
  physio: 'category.physio',
  gear: 'category.gear',
  sports_media: 'category.sports_media',
  // `other` cannot be a catalogue key: it is the plural form (src/i18n/ARCHITECTURE.md).
  other: 'category.notListed',
} as const);

const ONBOARDING_FIELDS = new Set<string>(Object.values(ONBOARDING_STEP_FIELDS).flat());

/** Step that holds a field, so an API error on it sends the user back there. */
export const getStepForField = (field: string): OnboardingStepKey | null =>
  ONBOARDING_STEPS.find((step) =>
    ONBOARDING_STEP_FIELDS[step].includes(field as keyof OnboardingFormValues),
  ) ?? null;

/** API field errors render under the input only for fields the form shows. */
export const isOnboardingVisibleField = (path: string) => ONBOARDING_FIELDS.has(path);

/** A new account starts from the signed-in user's name and e-mail as the contact. */
export const emptyOnboardingValues = (
  user?: Pick<User, 'firstName' | 'lastName' | 'email'> | null,
): OnboardingFormValues => ({
  displayName: '',
  legalName: '',
  taxId: '',
  category: '',
  description: '',
  logoUrl: '',
  contactName: [user?.firstName, user?.lastName].filter(Boolean).join(' '),
  contactEmail: user?.email ?? '',
  contactPhone: '',
  city: '',
  state: '',
  website: '',
  instagram: '',
  acceptTerms: false,
});

/** Edit mode starts from the saved account, with the document and the phone formatted. */
export const fromPartner = (partner: Partner): OnboardingFormValues => ({
  displayName: partner.displayName,
  legalName: partner.legalName,
  taxId: format.taxId(partner.taxId),
  category: partner.category,
  description: partner.description ?? '',
  logoUrl: partner.logoUrl ?? '',
  contactName: partner.contactName,
  contactEmail: partner.contactEmail,
  contactPhone: partner.contactPhone ? format.phoneNumber(partner.contactPhone) : '',
  city: partner.city ?? '',
  state: partner.state ?? '',
  website: partner.website ?? '',
  instagram: partner.instagram ? `@${partner.instagram}` : '',
  acceptTerms: true,
});

const blankToNull = (value: string) => value.trim() || null;

/** Body for `PUT /partners/me`; blank optional fields go as null. */
export const toPartnerAccountBody = (values: OnboardingFormValues): PartnerAccountBody => ({
  displayName: values.displayName.trim(),
  legalName: values.legalName.trim(),
  taxId: onlyDigits(values.taxId),
  category: values.category as PartnerCategory,
  contactName: values.contactName.trim(),
  contactEmail: values.contactEmail.trim(),
  contactPhone: values.contactPhone ? onlyDigits(values.contactPhone) || null : null,
  description: blankToNull(values.description),
  logoUrl: blankToNull(values.logoUrl),
  city: blankToNull(values.city),
  state: blankToNull(values.state),
  website: blankToNull(values.website),
  instagram: blankToNull(values.instagram),
});

/** Body for `POST /partners/signup`. */
export const toCreatePartnerAccountRequest = (
  values: OnboardingFormValues,
): CreatePartnerAccountRequest => ({ ...toPartnerAccountBody(values), acceptTerms: true });
